import React, { useEffect, useState } from 'react';
import { api } from '../api.js';
import { Plus, Trash2, KeyRound } from 'lucide-react';
import { useT } from '../i18n/useT.js';
import { useAlert } from '../components/AlertProvider.jsx';

export default function UsersPage() {
  const t = useT();
  const { showAlert, showConfirm } = useAlert();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', role: 'editor' });
  const [updatingRoleId, setUpdatingRoleId] = useState(null);
  const [passwordModal, setPasswordModal] = useState({ open: false, userId: '', email: '' });
  const [newPassword, setNewPassword] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.getUsers();
      setUsers(res.items || []);
    } catch (err) {
      showAlert(err.message || t('users.errors.load', 'Could not load users'), { tone: 'error', title: t('billing.loadFailed', 'Failed') });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const created = await api.createUser(form);
      setUsers((prev) => [created, ...prev]);
      setForm({ email: '', password: '', role: 'editor' });
      setShowCreate(false);
    } catch (err) {
      showAlert(err.message || t('users.errors.create', 'Could not create user'), { tone: 'error', title: t('billing.loadFailed', 'Failed') });
    }
  };

  const handleDelete = async (id, email) => {
    const accepted = await showConfirm(t('users.messages.confirmDelete', 'Delete user {{email}}?', { email }), {
      title: t('users.tooltips.deleteUser', 'Delete user'),
      confirmLabel: t('billing.yes', 'Yes'),
      cancelLabel: t('billing.cancel', 'Cancel'),
      tone: 'warning',
    });
    if (!accepted) return;
    try {
      await api.deleteUser(id);
      setUsers((prev) => prev.filter((u) => u.id !== id));
    } catch (err) {
      showAlert(err.message || t('users.errors.delete', 'Could not delete user'), { tone: 'error', title: t('billing.loadFailed', 'Failed') });
    }
  };

  const handleRoleChange = async (userId, role) => {
    setUpdatingRoleId(userId);
    try {
      const updated = await api.updateUser(userId, { role });
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, role: updated.role } : u)));
    } catch (err) {
      showAlert(err.message || t('users.errors.updateRole', 'Could not update role'), { tone: 'error', title: t('billing.loadFailed', 'Failed') });
    } finally {
      setUpdatingRoleId(null);
    }
  };

  const openPasswordModal = (userId, email) => {
    setNewPassword('');
    setPasswordModal({ open: true, userId, email });
  };

  const closePasswordModal = () => {
    setNewPassword('');
    setPasswordModal({ open: false, userId: '', email: '' });
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      showAlert(t('users.errors.passwordMin', 'Password must be at least 8 characters'), { tone: 'warning', title: t('billing.settingsTitle', 'Validation') });
      return;
    }
    try {
      await api.updateUser(passwordModal.userId, { password: newPassword });
      closePasswordModal();
      showAlert(t('users.messages.passwordUpdated', 'Password updated'), { tone: 'success', title: t('billing.settingsSaved', 'Saved') });
    } catch (err) {
      showAlert(err.message || t('users.errors.updatePassword', 'Could not update password'), { tone: 'error', title: t('billing.loadFailed', 'Failed') });
    }
  };

  const subscriptionStatusLabel = (status) => {
    const map = {
      active: t('billing.statusActive', 'Active'),
      past_due: t('billing.statusPastDue', 'Past Due'),
      suspended: t('billing.statusSuspended', 'Suspended'),
      canceled: t('billing.statusCanceled', 'Canceled'),
      trialing: t('billing.statusTrialing', 'Trialing'),
      pending_payment: t('billing.statusPendingPayment', 'Pending Payment'),
    };
    return map[status] || status;
  };

  const subscriptionStatusClass = (status) => {
    if (status === 'active') return 'is-active';
    if (status === 'pending_payment' || status === 'past_due') return 'is-warning';
    if (status === 'trialing') return 'is-info';
    if (status === 'suspended') return 'is-danger';
    return 'is-muted';
  };

  return (
    <>
      <div className="toolbar">
        <h2>{t('users.title', 'Users')}</h2>
        <div className="toolbar-actions">
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            <Plus size={14} style={{ marginRight: 4 }} /> {t('users.newUser', 'New User')}
          </button>
        </div>
      </div>

      <div className="page-content">
        {loading ? (
          <p>{t('users.loading', 'Loading...')}</p>
        ) : users.length === 0 ? (
          <div className="empty-state">
            <p>{t('users.empty', 'No users yet')}</p>
            <button className="btn-primary" style={{ marginTop: 12 }} onClick={() => setShowCreate(true)}>
              {t('users.createFirst', 'Create first user')}
            </button>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>{t('users.table.email', 'Email')}</th>
                <th>{t('users.table.role', 'Role')}</th>
                <th>{t('users.table.activePlan', 'Active plan')}</th>
                <th>{t('users.table.created', 'Created')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td>{u.email}</td>
                  <td style={{ minWidth: 140 }}>
                    <select
                      value={u.role}
                      disabled={updatingRoleId === u.id}
                      onChange={(e) => handleRoleChange(u.id, e.target.value)}
                    >
                      <option value="admin">admin</option>
                      <option value="editor">editor</option>
                      <option value="viewer">viewer</option>
                    </select>
                  </td>
                  <td>
                    {u.active_plan_name ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span>{u.active_plan_name}</span>
                        {u.subscription_status && (
                          <span className={`billing-status-pill ${subscriptionStatusClass(u.subscription_status)}`}>
                            {subscriptionStatusLabel(u.subscription_status)}
                          </span>
                        )}
                      </div>
                    ) : t('users.table.noPlan', 'No plan')}
                  </td>
                  <td>{new Date(u.created_at).toLocaleDateString()}</td>
                  <td style={{ display: 'flex', gap: 8 }}>
                    <button className="btn-secondary" onClick={() => openPasswordModal(u.id, u.email)} title={t('users.tooltips.changePassword', 'Change password')}>
                      <KeyRound size={14} />
                    </button>
                    <button className="btn-secondary" onClick={() => handleDelete(u.id, u.email)} title={t('users.tooltips.deleteUser', 'Delete user')}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t('users.newUser', 'New User')}</h3>
            <form onSubmit={handleCreate}>
              <div className="form-group">
                <label>{t('users.table.email', 'Email')}</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t('users.modal.password', 'Password')}</label>
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
                  required
                  minLength={8}
                />
              </div>
              <div className="form-group">
                <label>{t('users.modal.role', 'Role')}</label>
                <select
                  value={form.role}
                  onChange={(e) => setForm((prev) => ({ ...prev, role: e.target.value }))}
                >
                  <option value="admin">admin</option>
                  <option value="editor">editor</option>
                  <option value="viewer">viewer</option>
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowCreate(false)}>
                  {t('users.modal.cancel', 'Cancel')}
                </button>
                <button type="submit" className="btn-primary">{t('users.modal.create', 'Create')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {passwordModal.open && (
        <div className="modal-overlay" onClick={closePasswordModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t('users.modal.changePassword', 'Change password')}</h3>
            <p style={{ marginBottom: 12, color: 'var(--text-secondary)', fontSize: 13 }}>
              {t('users.modal.user', 'User')}: {passwordModal.email}
            </p>
            <form onSubmit={submitPassword}>
              <div className="form-group">
                <label>{t('users.modal.newPassword', 'New password')}</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={closePasswordModal}>
                  {t('users.modal.cancel', 'Cancel')}
                </button>
                <button type="submit" className="btn-primary">{t('users.modal.savePassword', 'Save password')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
